import { EntityMetadata } from "../metadata/EntityMetadata"
import { ColumnMetadata } from "../metadata/ColumnMetadata"
import { ForeignKeyMetadata } from "../metadata/ForeignKeyMetadata"
import type { DataSource } from "../data-source/DataSource"
import { IndexMetadata } from "../metadata/IndexMetadata"
import type { DataSourceOptions } from "../data-source/DataSourceOptions"

/**
 * Creates EntityMetadata for junction tables of the closure entities.
 * Closure junction tables are tables generated by closure entities.
 */
export class ClosureJunctionEntityMetadataBuilder {
    // -------------------------------------------------------------------------
    // Constructor
    // -------------------------------------------------------------------------

    constructor(private dataSource: DataSource) {}

    // -------------------------------------------------------------------------
    // Public Methods
    // -------------------------------------------------------------------------

    /**
     * Builds EntityMetadata for the closure junction of the given closure entity.
     *
     * @param parentClosureEntityMetadata
     */
    build(parentClosureEntityMetadata: EntityMetadata) {
        // create entity metadata itself
        type SchemaCapableOptions = Extract<
            DataSourceOptions,
            { schema?: string }
        >
        const { schema: dataSourceSchema } = this.dataSource.driver
            .options as SchemaCapableOptions

        const entityMetadata = new EntityMetadata({
            parentClosureEntityMetadata: parentClosureEntityMetadata,
            dataSource: this.dataSource,
            args: {
                target: "",
                name:
                    parentClosureEntityMetadata.treeOptions?.closureTableName ??
                    parentClosureEntityMetadata.tableNameWithoutPrefix,
                type: "closure-junction",
                schema:
                    parentClosureEntityMetadata.treeOptions
                        ?.closureTableSchema ??
                    parentClosureEntityMetadata.schema ??
                    dataSourceSchema,
                database: parentClosureEntityMetadata.database,
            },
        })
        entityMetadata.build()

        // create ancestor and descendant columns for new closure junction table
        parentClosureEntityMetadata.primaryColumns.forEach((primaryColumn) => {
            entityMetadata.ownColumns.push(
                new ColumnMetadata({
                    entityMetadata: entityMetadata,
                    closureType: "ancestor",
                    referencedColumn: primaryColumn,
                    args: {
                        target: "",
                        mode: "virtual",
                        propertyName: parentClosureEntityMetadata.treeOptions
                            ?.ancestorColumnName
                            ? parentClosureEntityMetadata.treeOptions.ancestorColumnName(
                                  primaryColumn,
                              )
                            : primaryColumn.propertyName + "_ancestor",
                        options: {
                            primary: true,
                            length: primaryColumn.length,
                            type: primaryColumn.type,
                            unsigned: primaryColumn.unsigned,
                            precision: primaryColumn.precision,
                            scale: primaryColumn.scale,
                            charset: primaryColumn.charset,
                            collation: primaryColumn.collation,
                        },
                    },
                }),
            )
            entityMetadata.ownColumns.push(
                new ColumnMetadata({
                    entityMetadata: entityMetadata,
                    closureType: "descendant",
                    referencedColumn: primaryColumn,
                    args: {
                        target: "",
                        mode: "virtual",
                        propertyName: parentClosureEntityMetadata.treeOptions
                            ?.descendantColumnName
                            ? parentClosureEntityMetadata.treeOptions.descendantColumnName(
                                  primaryColumn,
                              )
                            : primaryColumn.propertyName + "_descendant",
                        options: {
                            primary: true,
                            length: primaryColumn.length,
                            type: primaryColumn.type,
                            unsigned: primaryColumn.unsigned,
                            precision: primaryColumn.precision,
                            scale: primaryColumn.scale,
                            charset: primaryColumn.charset,
                            collation: primaryColumn.collation,
                        },
                    },
                }),
            )
        })

        entityMetadata.ownIndices = [
            new IndexMetadata({
                entityMetadata: entityMetadata,
                columns: [entityMetadata.ownColumns[0]],
                args: {
                    target: entityMetadata.target,
                    synchronize: true,
                },
            }),
            new IndexMetadata({
                entityMetadata: entityMetadata,
                columns: [entityMetadata.ownColumns[1]],
                args: {
                    target: entityMetadata.target,
                    synchronize: true,
                },
            }),
        ]

        // if tree level column was defined by a closure entity then add it to the junction columns as well
        if (parentClosureEntityMetadata.treeLevelColumn) {
            entityMetadata.ownColumns.push(
                new ColumnMetadata({
                    entityMetadata: entityMetadata,
                    args: {
                        target: "",
                        mode: "virtual",
                        propertyName: "level",
                        options: {
                            type: this.dataSource.driver.mappedDataTypes
                                .treeLevel,
                        },
                    },
                }),
            )
        }

        // create junction table foreign keys
        // Note: CASCADE is not applied to mssql because it does not support multi cascade paths
        entityMetadata.foreignKeys = [
            new ForeignKeyMetadata({
                entityMetadata: entityMetadata,
                referencedEntityMetadata: parentClosureEntityMetadata,
                columns: [entityMetadata.ownColumns[0]],
                referencedColumns: parentClosureEntityMetadata.primaryColumns,
                onDelete:
                    this.dataSource.driver.options.type === "mssql"
                        ? "NO ACTION"
                        : "CASCADE",
            }),
            new ForeignKeyMetadata({
                entityMetadata: entityMetadata,
                referencedEntityMetadata: parentClosureEntityMetadata,
                columns: [entityMetadata.ownColumns[1]],
                referencedColumns: parentClosureEntityMetadata.primaryColumns,
                onDelete:
                    this.dataSource.driver.options.type === "mssql"
                        ? "NO ACTION"
                        : "CASCADE",
            }),
        ]

        return entityMetadata
    }
}
